import { getStore } from '@/store';

/**
 * Retrieves the current authenticated user from the Vuex store.
 *
 * @returns {Object} The current user object from the store or an empty object if unavailable.
 */
function getCurrentUser() {
  const store = getStore();

  return store.getters.user || null;
}

/**
 * Returns the list of groups (roles) the user belongs to.
 *
 * @param {Object} user - The user object.
 * @returns {string[]} An array of group names (roles).
 */
function getUserGroups(user) {
  return Array.isArray(user?.groups) ? user.groups : [];
}

/**
 * Checks whether the user has admin privileges.
 *
 * @param {Object} [user=getCurrentUser()] - The user object to check.
 * @returns {boolean} `true` if the user is in the 'admin' or 'yester.admin' group, otherwise `false`.
 */
export function isAdmin(user = getCurrentUser()) {
  const groups = getUserGroups(user);

  return groups.includes('admin') || groups.includes('yester.admin');
}

/**
 * Checks whether the user is an employee (employee group).
 *
 * @param {Object} [user=getCurrentUser()] - The user object to check.
 * @returns {boolean} `true` if the user is in the `employee`, otherwise `false`.
 */
export function isEmployee(user = getCurrentUser()) {
  const groups = getUserGroups(user);

  return groups.includes('Toot');
}

/**
 * Checks if the current user id matched a given userId.
 *
 * @param userId The userId to compare with current logged in userId.
 * @returns {boolean}
 */
export function isCurrentUser(userId) {
  const currentUser = getCurrentUser();

  return userId === currentUser.id;
}

/**
 * Determines if the user has a specific role.
 *
 * @param {string} role - The role to check for.
 * @param {Object} [user=getCurrentUser()] - The user object to check.
 * @returns {boolean} `true` if the user has the given role, otherwise `false`.
 */
export function hasRole(role, user = getCurrentUser()) {
  return getUserGroups(user).includes(role);
}

/**
 * Determines if the user has at least one of the specified roles.
 *
 * @param {string[]} roles - An array of roles to check.
 * @param {Object} [user=getCurrentUser()] - The user object to check.
 * @returns {boolean} `true` if the user has any of the roles, or if the roles list is empty.
 */
export function hasAnyRole(roles = [], user = getCurrentUser()) {
  if (!roles.length) {
    return true;
  }

  const userGroups = getUserGroups(user);

  return roles.some((role) => userGroups.includes(role));
}

/**
* Determines if the user has permission based on a list of required roles.
* Grants access if the user is an admin or has any of the required roles.
*
* @param {string[]} requiredRoles - An array of roles that are permitted access.
* @param {Object} [user=getCurrentUser()] - The user object to check.
* @returns {boolean} `true` if the user has permission, otherwise `false`.
*/
export function hasPermission(requiredRoles = [], user = getCurrentUser()) {
  return isAdmin(user) || hasAnyRole(requiredRoles, user);
}
